import type { ReactNode } from 'react'
import { Boton } from '@/components/ui/Boton'
import {
  Dialogo,
  DialogoCerrar,
  DialogoContenido,
  DialogoCuerpo,
  DialogoDescripcion,
  DialogoEncabezado,
  DialogoPie,
  DialogoTitulo,
} from '@/components/ui/Dialogo'
import { ErrorApi } from '@/lib/api'

interface ModalMarcoProps {
  abierto: boolean
  alCerrar: () => void
  titulo: string
  descripcion?: string
  /** Etiqueta del botón principal; sin él, sólo Cerrar (Equipo RASTRO). */
  guardar?: string
  ocupado?: boolean
  /** Error general del envío, debajo del cuerpo. */
  error?: string | null
  alEnviar?: () => void
  /** Cualquier tecleo: los errores por campo se limpian hasta el próximo envío. */
  alCambiar?: () => void
  peligro?: boolean
  children: ReactNode
}

/**
 * C8 — modal de un solo paso: título, X (escritorio), Cancelar y Guardar.
 * Un `<form>` envuelve cuerpo y pie para que Enter envíe.
 */
export function ModalMarco({
  abierto,
  alCerrar,
  titulo,
  descripcion,
  guardar,
  ocupado = false,
  error,
  alEnviar,
  alCambiar,
  peligro = false,
  children,
}: ModalMarcoProps) {
  return (
    <Dialogo
      open={abierto}
      onOpenChange={(o) => {
        if (!o) alCerrar()
      }}
    >
      <DialogoContenido aria-busy={ocupado}>
        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(e) => {
            e.preventDefault()
            alEnviar?.()
          }}
          onChange={alCambiar}
          noValidate
        >
          <DialogoEncabezado>
            <DialogoTitulo className="text-xl font-semibold tracking-tight">
              {titulo}
            </DialogoTitulo>
            {descripcion ? (
              <DialogoDescripcion className="text-sm text-tinta-suave">
                {descripcion}
              </DialogoDescripcion>
            ) : (
              <DialogoDescripcion className="sr-only">{titulo}</DialogoDescripcion>
            )}
          </DialogoEncabezado>
          <DialogoCuerpo className="flex flex-col gap-4">
            {children}
            {error && (
              <p
                role="alert"
                className="rounded-xl bg-nuevo/10 px-3.5 py-2.5 text-sm text-nuevo"
              >
                {error}
              </p>
            )}
          </DialogoCuerpo>
          <DialogoPie>
            <DialogoCerrar asChild>
              <Boton variante="secundario" disabled={ocupado}>
                {guardar ? 'Cancelar' : 'Cerrar'}
              </Boton>
            </DialogoCerrar>
            {guardar && (
              <Boton
                type="submit"
                disabled={ocupado}
                className={peligro ? 'bg-nuevo hover:bg-nuevo/90' : undefined}
              >
                {guardar}
              </Boton>
            )}
          </DialogoPie>
        </form>
      </DialogoContenido>
    </Dialogo>
  )
}

/** Mensaje para la persona a partir de un error de la API o de red (§9). */
export function mensajeDeError(e: unknown): string {
  if (e instanceof ErrorApi && e.status < 500) return e.message
  return 'No pudimos guardar. Revisá tu conexión y probá de nuevo.'
}
